import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';

export function useTransferPartnerGuideFilters(page, transferPrograms, userPrograms) {
    // --- Mobile detection ---
    const isMobile = ref(false);
    let mq;
    const updateIsMobile = () => {
        if (mq) isMobile.value = !mq.matches;
    };
    onMounted(() => {
        if (typeof window !== 'undefined') {
            mq = window.matchMedia('(min-width: 768px)');
            updateIsMobile();
            if (mq.addEventListener) {
                mq.addEventListener('change', updateIsMobile);
            } else if (mq.addListener) {
                mq.addListener(updateIsMobile);
            }
        }
    });
    onUnmounted(() => {
        if (mq) {
            if (mq.removeEventListener) {
                mq.removeEventListener('change', updateIsMobile);
            } else if (mq.removeListener) {
                mq.removeListener(updateIsMobile);
            }
        }
    });

    // --- Filters ---
    const filters = computed(() => page.props.filters || { search: '', type: '', favorites: false });

    const programTypeOptions = [
        { id: 'airline', name: 'Airline' },
        { id: 'hotel', name: 'Hotel' },
        { id: 'rail', name: 'Rail' },
        { id: 'flexible_currency', name: 'Flexible currency' },
    ];

    const addedToMyPointsOptions = computed(() => {
        const users = [];
        userPrograms.value.forEach((userProgram) => {
            users.push({ id: userProgram.user_id.toString(), name: userProgram.user_name });
        });
        return users;
    });

    const selectedProgramTypes = ref([]);
    const selectedAddedToMyPoints = ref([]);
    const singleUserToggle = ref(false);
    const isSingleUser = computed(() => addedToMyPointsOptions.value.length <= 1);

    // Initialize from URL filters
    if (filters.value.types && filters.value.types.length > 0) {
        selectedProgramTypes.value = programTypeOptions.filter((option) =>
            filters.value.types.includes(option.id)
        );
    } else if (filters.value.type) {
        const selectedType = programTypeOptions.find((option) => option.id === filters.value.type);
        if (selectedType) selectedProgramTypes.value = [selectedType];
    }

    if (filters.value.users && filters.value.users.length > 0) {
        selectedAddedToMyPoints.value = addedToMyPointsOptions.value.filter((option) =>
            filters.value.users.includes(option.id)
        );
    }

    if (isSingleUser.value && filters.value.users && filters.value.users.length > 0) {
        singleUserToggle.value = true;
    }

    // --- Search ---
    const searchQuery = ref(filters.value.search);
    const debouncedQuery = ref(searchQuery.value);
    let searchDebounceTimeout = null;

    watch(searchQuery, (val) => {
        if (searchDebounceTimeout) clearTimeout(searchDebounceTimeout);
        searchDebounceTimeout = setTimeout(() => {
            debouncedQuery.value = val;
        }, 250);
    });

    // --- Sort / Group ---
    const sortOptions = [
        { id: 'default', name: 'Default sort' },
        { id: 'name_asc', name: 'Name A-Z' },
        { id: 'name_desc', name: 'Name Z-A' },
        { id: 'added_newest', name: 'Added (newest first)' },
        { id: 'added_oldest', name: 'Added (oldest first)' },
    ];
    const selectedSortOption = ref(sortOptions[0]);

    const groupOptions = [
        { id: 'no_grouping', name: 'No grouping' },
        { id: 'program_type', name: 'Program type' },
    ];
    const selectedGroupOption = ref(groupOptions[0]);

    const sortButtonText = computed(() => {
        if (selectedSortOption.value.id === 'default') return 'Sort';
        switch (selectedSortOption.value.id) {
            case 'name_asc': return 'Sort by name A-Z';
            case 'name_desc': return 'Sort by name Z-A';
            case 'added_newest': return 'Sort by added (newest first)';
            case 'added_oldest': return 'Sort by added (oldest first)';
            default: return `Sort by ${selectedSortOption.value.name.toLowerCase()}`;
        }
    });

    const groupButtonText = computed(() => {
        if (selectedGroupOption.value.id === 'no_grouping') return 'Group';
        return `Group by ${selectedGroupOption.value.name.toLowerCase()}`;
    });

    // Mobile overlay state
    const showSortOverlay = ref(false);
    const showGroupOverlay = ref(false);
    const tempSelectedSort = ref(selectedSortOption.value);
    const tempSelectedGroup = ref(selectedGroupOption.value);

    const openSortOverlay = () => { tempSelectedSort.value = selectedSortOption.value; showSortOverlay.value = true; };
    const closeSortOverlay = () => { showSortOverlay.value = false; };
    const applySortOverlay = () => { selectedSortOption.value = tempSelectedSort.value; showSortOverlay.value = false; };

    const openGroupOverlay = () => { tempSelectedGroup.value = selectedGroupOption.value; showGroupOverlay.value = true; };
    const closeGroupOverlay = () => { showGroupOverlay.value = false; };
    const applyGroupOverlay = () => { selectedGroupOption.value = tempSelectedGroup.value; showGroupOverlay.value = false; };

    const selectSortOption = (option, closePopover) => { selectedSortOption.value = option; closePopover(); };
    const selectGroupOption = (option, closePopover) => { selectedGroupOption.value = option; closePopover(); };

    // --- Loading ---
    const loading = ref(false);

    // --- Mobile filters ---
    const isMobileFiltersOpen = ref(false);

    // --- Pagination ---
    const currentPage = ref(1);
    const itemsPerPage = computed(() => (isMobile.value ? 48 : 24));

    const allPrograms = computed(() => {
        let data = transferPrograms.value.data || [];
        let programs = Array.isArray(data) ? [...data] : Object.values(data);

        if (debouncedQuery.value && !filters.value.search) {
            const searchTerm = debouncedQuery.value.toLowerCase();
            programs = programs.filter((program) => program.name.toLowerCase().includes(searchTerm));
        }

        if (isSingleUser.value && singleUserToggle.value) {
            // No My Points programs means nothing matches, not "skip the filter" (SUPPORT-830).
            const userProgramIds = (userPrograms.value[0]?.programs || []).map((p) => p.id);
            programs = programs.filter((program) => userProgramIds.includes(program.id));
        } else if (!isSingleUser.value && selectedAddedToMyPoints.value.length > 0) {
            const selectedUserIds = selectedAddedToMyPoints.value.map((user) => parseInt(user.id));
            const selectedUserProgramIds = new Set();
            selectedUserIds.forEach((userId) => {
                const userData = userPrograms.value.find((up) => up.user_id === userId);
                if (userData) {
                    userData.programs.forEach((program) => selectedUserProgramIds.add(program.id));
                }
            });
            programs = programs.filter((program) => selectedUserProgramIds.has(program.id));
        }

        return programs;
    });

    const totalPages = computed(() => Math.ceil(allPrograms.value.length / itemsPerPage.value));

    const paginatedPrograms = computed(() => {
        const start = (currentPage.value - 1) * itemsPerPage.value;
        return allPrograms.value.slice(start, start + itemsPerPage.value);
    });

    const startIndex = computed(() => {
        if (allPrograms.value.length === 0) return 0;
        return (currentPage.value - 1) * itemsPerPage.value + 1;
    });
    const endIndex = computed(() => Math.min(currentPage.value * itemsPerPage.value, allPrograms.value.length));

    const handlePageChange = (p) => { currentPage.value = p; };

    watch([itemsPerPage, allPrograms], () => {
        const tp = Math.max(1, Math.ceil(allPrograms.value.length / itemsPerPage.value));
        if (currentPage.value > tp) currentPage.value = tp;
    });

    const maxVisibleButtonsMobile = 5;
    const startPageMobile = computed(() => {
        const maxPage = Math.max(1, totalPages.value);
        if (currentPage.value === 1) return 1;
        let start = currentPage.value - Math.floor(maxVisibleButtonsMobile / 2);
        if (start + maxVisibleButtonsMobile > maxPage) {
            start = Math.max(1, maxPage - maxVisibleButtonsMobile + 1);
        }
        return Math.max(1, start);
    });
    const mobilePages = computed(() => {
        const range = [];
        const maxPage = Math.max(1, totalPages.value);
        for (let i = startPageMobile.value; i <= Math.min(startPageMobile.value + maxVisibleButtonsMobile - 1, maxPage); i++) {
            range.push(i);
        }
        return range;
    });

    // --- Sorted / Grouped ---
    const sortedPrograms = computed(() => {
        let programs = filters.value.favorites ? [...allPrograms.value] : [...paginatedPrograms.value];
        switch (selectedSortOption.value.id) {
            case 'name_asc': return programs.sort((a, b) => a.name.localeCompare(b.name));
            case 'name_desc': return programs.sort((a, b) => b.name.localeCompare(a.name));
            case 'added_newest': return programs.sort((a, b) => {
                const dateA = a.favorited_at ? new Date(a.favorited_at) : new Date(0);
                const dateB = b.favorited_at ? new Date(b.favorited_at) : new Date(0);
                return dateB - dateA;
            });
            case 'added_oldest': return programs.sort((a, b) => {
                const dateA = a.favorited_at ? new Date(a.favorited_at) : new Date(0);
                const dateB = b.favorited_at ? new Date(b.favorited_at) : new Date(0);
                return dateA - dateB;
            });
            default: return programs.sort((a, b) => {
                const dateA = a.favorited_at ? new Date(a.favorited_at) : new Date(0);
                const dateB = b.favorited_at ? new Date(b.favorited_at) : new Date(0);
                return dateB - dateA;
            });
        }
    });

    const groupedPrograms = computed(() => {
        if (selectedGroupOption.value.id === 'no_grouping') return null;
        if (selectedGroupOption.value.id === 'program_type') {
            const programs = sortedPrograms.value;
            const grouped = {};
            programs.forEach((program) => {
                const type = program.type;
                if (!grouped[type]) grouped[type] = [];
                grouped[type].push(program);
            });
            return grouped;
        }
        return null;
    });

    // --- Filter toggle methods ---
    const isProgramTypeSelected = (option) => selectedProgramTypes.value.some((type) => type.id === option.id);
    const isAddedToMyPointsSelected = (option) => selectedAddedToMyPoints.value.some((point) => point.id === option.id);

    const hasFilterSelections = computed(() =>
        selectedProgramTypes.value.length > 0 ||
        selectedAddedToMyPoints.value.length > 0 ||
        singleUserToggle.value === true
    );

    const activeFiltersCount = computed(() => {
        let count = 0;
        count += selectedProgramTypes.value.length;
        count += selectedAddedToMyPoints.value.length;
        count += singleUserToggle.value ? 1 : 0;
        return count;
    });

    const updateFilters = () => {
        const params = {};
        if (searchQuery.value) params.search = searchQuery.value;
        if (selectedProgramTypes.value.length > 0) {
            params.types = selectedProgramTypes.value.map((type) => type.id);
        }
        if (selectedAddedToMyPoints.value.length > 0) {
            params.users = selectedAddedToMyPoints.value.map((user) => user.id);
        }
        if (filters.value.favorites) params.favorites = true;

        router.get('/resources/transfer-partners-guide', params, {
            preserveState: true,
            preserveScroll: true,
            onStart: () => { loading.value = true; },
            onFinish: () => { loading.value = false; },
        });
    };

    const toggleProgramTypeSelection = (option) => {
        const index = selectedProgramTypes.value.findIndex((type) => type.id === option.id);
        if (index > -1) {
            selectedProgramTypes.value.splice(index, 1);
        } else {
            selectedProgramTypes.value.push(option);
        }
        updateFilters();
    };

    const toggleAddedToMyPointsSelection = (option) => {
        const index = selectedAddedToMyPoints.value.findIndex((point) => point.id === option.id);
        if (index > -1) {
            selectedAddedToMyPoints.value.splice(index, 1);
        } else {
            selectedAddedToMyPoints.value.push(option);
        }
        updateFilters();
    };

    const resetProgramTypeFilter = () => { selectedProgramTypes.value = []; updateFilters(); };
    const resetAddedToMyPointsFilter = () => { selectedAddedToMyPoints.value = []; updateFilters(); };

    const clearAllFilters = () => {
        selectedProgramTypes.value = [];
        selectedAddedToMyPoints.value = [];
        searchQuery.value = '';
        singleUserToggle.value = false;
        updateFilters();
    };

    const clearSearch = () => {
        searchQuery.value = '';
        debouncedQuery.value = '';
        currentPage.value = 1;
        updateFilters();
    };

    const toggleFavoritesFilter = () => {
        const newFavoritesState = !filters.value.favorites;
        selectedProgramTypes.value = [];
        selectedAddedToMyPoints.value = [];
        searchQuery.value = '';
        selectedSortOption.value = sortOptions[0];
        selectedGroupOption.value = groupOptions[0];

        const params = {};
        if (newFavoritesState) params.favorites = true;
        params.page = 1;

        router.get('/resources/transfer-partners-guide', params, {
            preserveState: false,
            preserveScroll: false,
            onStart: () => { loading.value = true; },
            onFinish: () => { loading.value = false; },
        });
    };

    const applyMobileFilters = () => {
        currentPage.value = 1;
        updateFilters();
        isMobileFiltersOpen.value = false;
    };

    const handleClearAllMobile = () => {
        selectedProgramTypes.value = [];
        selectedAddedToMyPoints.value = [];
        singleUserToggle.value = false;
        updateFilters();
    };

    // --- Helpers ---
    const formatProgramType = (type) => {
        const typeMap = { flexible_currency: 'Flexible currency', airline: 'Airline', hotel: 'Hotel', rail: 'Rail' };
        return typeMap[type] || type;
    };

    const getTypeDisplayInfo = (type) => {
        const typeMap = {
            flexible_currency: { name: 'Flexible Currency', icon: 'credit-card' },
            airline: { name: 'Airlines', icon: 'airplane' },
            hotel: { name: 'Hotels', icon: 'building' },
            rail: { name: 'Rail', icon: 'train' },
        };
        return typeMap[type] || { name: type, icon: 'star' };
    };

    return {
        // State
        isMobile,
        filters,
        loading,
        searchQuery,
        debouncedQuery,
        selectedProgramTypes,
        selectedAddedToMyPoints,
        singleUserToggle,
        isSingleUser,
        programTypeOptions,
        addedToMyPointsOptions,
        isMobileFiltersOpen,
        // Sort / Group
        sortOptions,
        selectedSortOption,
        groupOptions,
        selectedGroupOption,
        sortButtonText,
        groupButtonText,
        showSortOverlay,
        showGroupOverlay,
        tempSelectedSort,
        tempSelectedGroup,
        // Pagination
        currentPage,
        itemsPerPage,
        totalPages,
        allPrograms,
        paginatedPrograms,
        startIndex,
        endIndex,
        mobilePages,
        // Computed
        sortedPrograms,
        groupedPrograms,
        hasFilterSelections,
        activeFiltersCount,
        // Methods
        handlePageChange,
        isProgramTypeSelected,
        isAddedToMyPointsSelected,
        toggleProgramTypeSelection,
        toggleAddedToMyPointsSelection,
        resetProgramTypeFilter,
        resetAddedToMyPointsFilter,
        clearAllFilters,
        clearSearch,
        updateFilters,
        toggleFavoritesFilter,
        applyMobileFilters,
        handleClearAllMobile,
        selectSortOption,
        selectGroupOption,
        openSortOverlay,
        closeSortOverlay,
        applySortOverlay,
        openGroupOverlay,
        closeGroupOverlay,
        applyGroupOverlay,
        formatProgramType,
        getTypeDisplayInfo,
    };
}
